import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Text, Flex, HStack } from '@chakra-ui/react';
import { Box, Button, Tooltip } from 'components';
import { withNamespaces, Trans } from 'react-i18next';
import { observer } from 'mobx-react';
import Heading from 'components/Heading';
import Paragraph from 'components/Paragraph';
import Icon from 'components/Icon';

import {
  LargePanelSectionWrapper,
  CollapseWrapper,
  ContentWrapper,
} from 'apps/rfq/rfqForm/views/onboarding.styled';
import { IntermediatorSwitcherDescription } from 'apps/client/components/ChangePortalBanner';
import {
  StatusDivider /* , Divider */,
} from 'apps/rfq/rfqPage/views/openRFQ.styled';

import { useClientStore } from 'apps/hooks';
import { SpaceLink } from 'apps/shared';
import { StatusSwitcher, StatusDisplay } from 'apps/components/StatusSwitcher';
import { frameStore } from 'apps/components/Frame/appManager';

const copyRFQ = 'Duplicate RFQ';

const RFQIntro = observer(props => {
  const clientStore = useClientStore();
  const { rfqManager, rfqApp, isIntermediator } = props;
  const {
    state,
    intermediatorSession: { shareFile },
  } = rfqApp;
  const hasIntermediatorRole = clientStore.hasRole('INTERMEDIATOR');
  const customerLink = window.location.href.replace('intermediator/', '');
  const isIntermediatorMode = clientStore.currentPortal === 'INTERMEDIATOR';

  useEffect(() => {
    if (shareFile) {
      frameStore.SetFile(shareFile?.revision?.file);
    }

    return () => {
      frameStore.SetFile(null);
    };
  }, [shareFile]);

  const handleClone = () => {
    clientStore.routerStore.navigate('workspace.make-part.file', {
      sharedFileId: shareFile.id,
      panel: 'rfq',
      cloneId: rfqManager.rfqId,
      spacePath: clientStore.slugFromRouteOrSpace,
    });
  };

  return (
    <LargePanelSectionWrapper first hasBanner={hasIntermediatorRole}>
      <Text
        as='small'
        fontSize='md'
        fontWeight='bold'
        color='gray'
        mb={2}
        mt={hasIntermediatorRole ? 2 : 0}
      >
        Request for Quotation
        {hasIntermediatorRole && (
          <Text
            ml={2}
            as='span'
            fontWeight='normal'
            color='blue'
            cursor='pointer'
            _hover={{ textDecoration: 'underline' }}
            onClick={() => rfqApp.CopyLinkToClipboard(customerLink)}
          >
            Customer link{' '}
            <Icon color='#0f2eff' name='link' viewBox='0 0 32 32' />
          </Text>
        )}
      </Text>
      <Box mt={2} mb={4}>
        <HStack justify='space-between'>
          <Heading level='1' size='xlarge' data-testid='rfqPage-filename'>
            <Trans>{shareFile.name}</Trans>
          </Heading>

          <Tooltip id={copyRFQ} place='bottom' delayShow={1000} />
          {!isIntermediatorMode && (
            <Button
              data-testid='rfq-clone-button'
              size='small'
              onClick={handleClone}
              icon='copy'
              outline
              data-tip={copyRFQ}
              data-for={copyRFQ}
            />
          )}
        </HStack>
        {hasIntermediatorRole && (
          <SpaceLink
            variant='primary'
            label='RFQ for'
            size='medium'
            space={rfqApp.customerSpace}
          />
        )}
      </Box>
      <IntermediatorSwitcherDescription className='mb-4' />
      <CollapseWrapper>
        <ContentWrapper>
          {isIntermediator ? (
            <Paragraph>
              <Trans>
                Please review the following RFQ, and either (1) assign it to one
                of our manufacturing partners for direct quoting, or (2) create
                a quote in the name of any manufacturer you&apos;ve engaged
                offline.
              </Trans>
            </Paragraph>
          ) : (
            <Paragraph>
              <Trans>
                Thank you, your RFQ has been received. One of our eningeering
                experts is evaluating your part, and will forward you quotes
                from our network within 24h.
              </Trans>
            </Paragraph>
          )}
          <>
            <StatusDivider />
            <Flex alignItems='center'>
              {isIntermediator ? (
                <StatusSwitcher
                  nextPossibleStates={rfqApp.nextPossibleStates}
                  value={state}
                  onChange={rfqManager.UpdateRFQState}
                  type='rfq'
                />
              ) : (
                <StatusDisplay status={state} type='rfq' />
              )}
            </Flex>
          </>
        </ContentWrapper>
      </CollapseWrapper>
      {/* <Divider /> */}
    </LargePanelSectionWrapper>
  );
});

RFQIntro.propTypes = {
  rfqApp: PropTypes.object,
  isIntermediator: PropTypes.bool,
};

export default withNamespaces()(RFQIntro);
